import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { getServerErrorMessage } from "utils/showMutationError";

/**
 * Undo a completion made on the wrong record (KIN-1942, backend KIN-1940).
 *
 * Exists for mistakes, not as a step in the normal flow, so the UI keeps it unobtrusive — but it
 * has to exist: completing a pregnancy changes what the patient's chart says about her, and
 * without this the only way back would be a database edit.
 *
 * A 409 here means it was not completed in the first place, which again is a state to show rather
 * than an error to report: whatever the user wanted to undo is already undone.
 */
export const useReopenPregnancy = ({ tenantVal, pregnancyId, onSuccess }) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/pregnancy/${pregnancyId}/reopen`
      );

      return response.data;
    },
    onSuccess: () => {
      toast.success("Pregnancy reopened.");
      queryClient.invalidateQueries({ queryKey: ["pregnancies"] });
      onSuccess?.();
    },
    onError: (error) => {
      if (error?.response?.status === 409) {
        toast("This pregnancy is not completed.");
        queryClient.invalidateQueries({ queryKey: ["pregnancies"] });
        onSuccess?.();

        return;
      }

      console.error(error);
      toast.error(
        getServerErrorMessage(error, "The pregnancy could not be reopened.")
      );
    },
  });
};
